import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaVolumeUp, FaVolumeMute, FaUpload, FaExpandArrowsAlt, FaTimes, FaChevronDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from './UpgradeQuotaModal';
import { saveTutorialActivity, checkExistingActivity, saveTutorialWithId, checkTutorialById, findLatestWaysOfSeeingJourneyIdForArtwork } from '../utils/activityService';
import { collection, query, where, getDocs, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import GridOverlay from './GridOverlay';

// Add custom CSS for 3D card flip effect
const cardFlipStyles = `
  .perspective-1000 {
    perspective: 1000px;
  }
  .transform-style-preserve-3d {
    transform-style: preserve-3d;
  }
  .backface-hidden {
    backface-visibility: hidden;
  }
  .rotate-y-180 {
    transform: rotateY(180deg);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = cardFlipStyles;
  document.head.appendChild(styleSheet);
}

// Artmosphere aesthetics brand palette via CSS variables (easy to theme app-wide)
// Defaults provided; can be overridden in global CSS: :root { --am-intro-start: ... }
const BRAND = {
  introStart: 'var(--am-intro-start, #e9f2ff)',
  introEnd: 'var(--am-intro-end, #eef3ff)',
  introText: 'var(--am-intro-text, #1e3a8a)',
  reminderStart: 'var(--am-reminder-start, #ecfdf5)',
  reminderEnd: 'var(--am-reminder-end, #e6f9f0)',
  reminderText: 'var(--am-reminder-text, #065f46)'
};

// Lightweight Artmosphere toast (no emojis)
const showArtmosphereToast = (message) => {
  try {
    const container = document.createElement('div');
    container.className = 'fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none';
    container.innerHTML = `
      <div class=\"bg-white/95 pointer-events-auto rounded-2xl shadow-2xl border-2 border-amber-200 px-6 py-4\"
           style=\"min-width:300px; max-width: 520px;\">
        <div class=\"text-center\">
          <div class=\"text-amber-800 font-semibold text-lg mb-1\">Success</div>
          <div class=\"text-amber-700\">${message}</div>
        </div>
      </div>`;
    document.body.appendChild(container);
    setTimeout(() => { try { document.body.removeChild(container); } catch (_) {} }, 2000);
  } catch (_) {}
};

const StepByStepLearning = ({
  transformedImage,
  styleInfo,
  onClose,
  userAge = 10,
  userId,
  onBackToResults,
  forceGenerate = false,
  hasTutorial = false,
  artworkId,
  existingTutorialData = null,
  originalImageUrl = null,
  /** Firestore userActivities document id from saveWaysOfSeeingJourney (not the image URL). */
  reimagineActivityId = null,
}) => {
  const navigate = useNavigate();
  const { showUpgradeModal, upgradeModalFeature, dismissUpgradeModal } = useFeatureQuota();
  const [tutorial, setTutorial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showStep11, setShowStep11] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [sharePromptOpen, setSharePromptOpen] = useState(false);
  /** 'uploaded' = success step, then 'share' = Artist Salon prompt */
  const [sharePromptPhase, setSharePromptPhase] = useState('uploaded');
  const sharePromptResolverRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    return false;
  });
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(3); // 3x3, 4x4, or 6x6
  const [showGridSizeSelector, setShowGridSizeSelector] = useState(false);
  const artworkImageRef = useRef(null);
  const expandedArtworkRef = useRef(null);
  const gridControlsRef = useRef(null);
  const gridSizeSelectorRef = useRef(null);
  
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
      console.log('📱 Mobile detection:', { width: window.innerWidth, isMobile: isMobileDevice, userAgent: navigator.userAgent.substring(0, 50) });
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const askSharePublicly = () =>
    new Promise((resolve) => {
      sharePromptResolverRef.current = resolve;
      setSharePromptPhase('uploaded');
      setSharePromptOpen(true);
    });

  // Age band for kid-friendly content
  const getAgeBand = (age) => {
    if (age < 8) return 'young_kids';
    if (age < 12) return 'older_kids';
    return 'teens';
  };

  useEffect(() => {
      console.log('StepByStepLearning component mounted!');
    console.log('🔍 Props received:', {
      hasTutorial,
      existingTutorialData,
      artworkId,
      styleInfo: styleInfo?.id
    });
    generateTutorial();
    setShowStep11(false); // Reset Step 11 visibility when component mounts
  }, []);

  const generateTutorial = async () => {
    try {
      setIsLoading(true);
      
      console.log('Starting tutorial generation with style:', styleInfo?.name, 'and artwork:', typeof artworkId === 'string' ? artworkId?.substring(0, 80) : artworkId);
      console.log('Style info details:', {
        id: styleInfo?.id,
        name: styleInfo?.name,
        description: styleInfo?.description?.substring(0, 50) + '...'
      });
      
      // ALWAYS check for existing tutorial first to prevent duplicate API calls
      console.log('🔍 Performing comprehensive duplicate check before API call...');
      console.log('🔍 Duplicate check parameters:', {
        styleId: styleInfo.id,
        styleName: styleInfo.name,
        artworkId: artworkId,
        hasExistingTutorialData: !!existingTutorialData,
        existingTutorialDataKeys: existingTutorialData ? Object.keys(existingTutorialData) : []
      });
      
      // First check if we have tutorial data passed directly as props
      if (existingTutorialData && existingTutorialData.tutorial) {
        console.log('✅ Found tutorial data in props, using it directly - NO API CALL');
        console.log('🔍 Props tutorial data:', {
          hasTutorial: !!existingTutorialData.tutorial,
          tutorialKeys: existingTutorialData.tutorial ? Object.keys(existingTutorialData.tutorial) : []
        });
        setTutorial(existingTutorialData.tutorial);
        setIsLoading(false);
        return;
      }
      
      // Check for existing tutorial in Firestore using tutorial ID
      console.log('🔍 Checking for existing tutorial in Firestore with:', {
        styleId: styleInfo.id,
        artworkId: artworkId
      });
      
      const existingTutorial = await checkTutorialById(
        styleInfo.id,
        artworkId
      );
      
      console.log('🔍 checkTutorialById result:', {
        found: !!existingTutorial,
        hasTutorial: existingTutorial ? !!existingTutorial.tutorial : false,
        tutorialId: existingTutorial ? existingTutorial.tutorialId : null
      });
      
      if (existingTutorial && existingTutorial.tutorial) {
        console.log('✅ Found existing tutorial in Firestore, using it - NO API CALL');
        setTutorial(existingTutorial.tutorial);
        setIsLoading(false);
        return;
      }
      
      // Note: Tutorials are saved to userActivities collection (reimagine activities), not separate tutorials collection
      // The checkTutorialById function above already handles this properly
      
      console.log('🔄 No existing tutorial found, proceeding with generation...');
      
      // Check for existing tutorial in Firestore first (if hasTutorial is true)
      if (hasTutorial) {
        console.log('📘 Using existing tutorial from Firestore - NO BACKEND COST');
        
        console.log('🔍 StepByStepLearning: Checking for existing tutorial with:', {
          styleId: styleInfo.id,
          artworkId: artworkId,
          hasTutorial: hasTutorial,
          existingTutorialData: existingTutorialData
        });
        
        // First check if we have tutorial data passed directly as props
        if (existingTutorialData && existingTutorialData.tutorial) {
          console.log('✅ Found tutorial data in props, using it directly');
          setTutorial(existingTutorialData.tutorial);
          setIsLoading(false);
          return;
        }
        
        // Get the existing tutorial from Firestore using tutorial ID
        const existingTutorial = await checkTutorialById(
          styleInfo.id,
          artworkId
        );
        
        if (existingTutorial && existingTutorial.tutorial) {
          console.log('✅ Found existing tutorial in Firestore, using it');
          setTutorial(existingTutorial.tutorial);
          setIsLoading(false);
          return;
        } else {
          console.log('⚠️ hasTutorial was true but no tutorial found in Firestore, falling back to generation');
        }
      }
      
      if (forceGenerate) {
        console.log('🔄 Force generating tutorial...');
      }
      
      console.log('🔄 Generating new tutorial...');
      
      // Tutorial generation should be allowed for already-generated style transfers.
      // Do NOT consume quota again here; style-transfer quota is charged at transform time.
      
      // CRITICAL: Use STYLE-TRANSFERRED artwork for tutorial analysis
      // The transformed artwork shows the final result in the target style, which helps generate
      // tutorials that are specific to how the artwork looks after style transformation
      // Prioritize transformedImage, but fallback to originalImageUrl if transformed is not available
      let processedImageUrl = (transformedImage && transformedImage.trim()) ? transformedImage : originalImageUrl;
      
      // Resolve relative paths to full URLs for production
      if (processedImageUrl && typeof processedImageUrl === 'string') {
        // If it's a relative path starting with /uploads, convert to full backend URL
        if (processedImageUrl.startsWith('/uploads') || processedImageUrl.startsWith('uploads')) {
          const backendBase = API_ENDPOINTS.backendBase || '';
          if (processedImageUrl.startsWith('/uploads')) {
            processedImageUrl = `${backendBase}${processedImageUrl}`;
          } else {
            processedImageUrl = `${backendBase}/${processedImageUrl}`;
          }
          console.log('🔗 Resolved uploads path to:', processedImageUrl.substring(0, 100) + '...');
        }
      }
      
      // Validate that we have a valid image URL
      if (!processedImageUrl || !processedImageUrl.trim()) {
        console.error('❌ No valid image URL available for tutorial generation');
        console.error('   originalImageUrl:', originalImageUrl);
        console.error('   transformedImage:', transformedImage);
        setIsLoading(false);
        throw new Error('No artwork image available. Please select an artwork first.');
      }
      
      console.log('🔍 Image info for tutorial generation:', {
        originalImageUrl: originalImageUrl ? originalImageUrl.substring(0, 100) + '...' : 'none',
        transformedImage: transformedImage ? transformedImage.substring(0, 100) + '...' : 'none',
        usingTransformed: !!(transformedImage && transformedImage.trim()),
        usingOriginal: !(transformedImage && transformedImage.trim()) && !!originalImageUrl,
        processedImageUrl: processedImageUrl.substring(0, 100) + '...',
        startsWithData: processedImageUrl.startsWith('data:image'),
        startsWithHttp: processedImageUrl.startsWith('http'),
        length: processedImageUrl.length,
        sizeKB: (processedImageUrl.length / 1024).toFixed(2)
      });
      
      // Use URL directly - backend can handle URLs without conversion
      if (processedImageUrl.startsWith('http')) {
        console.log('📥 Using URL directly (no conversion needed)');
      }
      
      // If it's a data URL, resize it
      if (processedImageUrl.startsWith('data:image')) {
        try {
          console.log('📏 Starting image resize...');
          const originalSize = processedImageUrl.length;
          const resizedImage = await resizeImage(processedImageUrl, 512); // Resize to 512px max
          processedImageUrl = resizedImage;
          console.log('✅ Image resized successfully:', {
            originalSize: (originalSize / 1024).toFixed(2) + 'KB',
            resizedSize: (resizedImage.length / 1024).toFixed(2) + 'KB'
          });
        } catch (resizeError) {
          console.warn('⚠️ Failed to resize image, using original:', resizeError);
        }
      } else {
        console.log('✅ Using URL directly - no resize needed');
      }
      
      console.log('Generating tutorial for:', {
        imageUrl: processedImageUrl.substring(0, 100) + '...',
        imageSize: (processedImageUrl.length / 1024).toFixed(2) + 'KB',
        styleId: styleInfo.id,
        styleName: styleInfo.name,
        ageBand: getAgeBand(userAge),
        userAge: userAge,
        usingTransformed: !!(transformedImage && transformedImage.trim()),
        usingOriginal: !(transformedImage && transformedImage.trim()) && !!originalImageUrl
      });
      
      console.log('🔗 API Endpoint:', API_ENDPOINTS.generateTutorial);
      console.log('📤 Sending tutorial generation request at:', new Date().toISOString());
      console.log('📤 Request payload:', {
        imageUrlLength: processedImageUrl.length,
        imageUrlPreview: processedImageUrl.substring(0, 100) + '...',
        styleId: styleInfo.id,
        styleName: styleInfo.name,
        ageBand: getAgeBand(userAge),
        userAge: userAge,
        forceFresh: forceGenerate
      });
      
      // Use background job processing (like style-transfer and reimagine) to prevent timeouts
      console.log('🚀 Using enqueueAndPoll for tutorial generation background processing');
      
      const requestStartTime = Date.now();
      let data;
      try {
        // Use enqueueAndPoll utility for background job processing
        const jobResult = await enqueueAndPoll(
          API_ENDPOINTS.generateTutorial,
          {
            imageUrl: processedImageUrl,
            styleId: styleInfo.id,
            styleName: styleInfo.name,
            ageBand: getAgeBand(userAge),
            userAge: userAge,
            forceFresh: forceGenerate // Only force fresh if explicitly requested
          },
          {
            interval: 2000, // Poll every 2 seconds
            maxAttempts: 150, // Max 5 minutes (150 * 2s)
            onProgress: (status) => {
              if (status.status === 'processing') {
                console.log('🔄 Tutorial generation in progress...');
              }
            }
          }
        );
        
        const requestDuration = Date.now() - requestStartTime;
        console.log(`✅ Tutorial generation job completed in ${requestDuration}ms`);
        
        // enqueueAndPoll returns the result from the worker
        // Worker returns: { success: true, tutorial, tutorialId, cached }
        data = {
          success: true,
          tutorial: jobResult.tutorial || jobResult.result?.tutorial,
          tutorialId: jobResult.tutorialId || jobResult.result?.tutorialId,
          cached: jobResult.cached || jobResult.result?.cached || false
        };
        
        if (!data.tutorial) {
          throw new Error('No tutorial data in job result');
        }
      } catch (pollError) {
        const requestDuration = Date.now() - requestStartTime;
        console.error(`❌ Tutorial generation failed after ${requestDuration}ms:`, pollError);
        throw pollError;
      }
      console.log('✅ Tutorial generated successfully:', data);
      console.log('📊 Tutorial data structure:', {
        hasTutorial: !!data.tutorial,
        hasSteps: !!(data.tutorial && data.tutorial.steps),
        stepsCount: data.tutorial ? data.tutorial.steps.length : 0,
        hasPalette: !!(data.tutorial && data.tutorial.palette),
        hasMaterials: !!(data.tutorial && data.tutorial.materials)
      });
      
      // Check if tutorial data is valid
      if (!data.tutorial) {
        console.error('❌ No tutorial data in response:', data);
        throw new Error('No tutorial data received from server');
      }
      
      // Remove emojis from step titles (in case backend didn't remove them)
      if (data.tutorial && data.tutorial.steps && Array.isArray(data.tutorial.steps)) {
        data.tutorial.steps = data.tutorial.steps.map(step => {
          if (step.title) {
            // Remove common emojis and special characters from titles
            step.title = step.title
              .replace(/[🛠️✏️🔍✨🌞🎯🌟💎🎉🤖]/g, '')
              .replace(/\s+/g, ' ')
              .trim();
          }
          return step;
        });
        console.log('🔧 Removed emojis from step titles in frontend');
      }
      
      // Log the first step details to see if they're specific
      if (data.tutorial && data.tutorial.steps && data.tutorial.steps[0]) {
        console.log('🔍 Step 1 details:', {
          title: data.tutorial.steps[0].title,
          tools: data.tutorial.steps[0].tools,
          instruction: data.tutorial.steps[0].instruction.substring(0, 100) + '...'
        });
      }
      
      // Check if this is a fallback tutorial
      if (data.tutorial && data.tutorial.steps) {
        // Better specificity detection - look for detailed, artwork-specific instructions
        const specificIndicators = [
          'person', 'horse', 'archway', 'trees', 'border', 'rider', 'clothing', 'tunic', 'dhoti',
          'mane', 'tail', 'body', 'head', 'arms', 'legs', 'hands', 'trunk', 'leaves',
          'yellow', 'orange', 'blue', 'white', 'green', 'brown', 'black', 'red',
          'center', 'top-center', 'left-edge', 'background', 'foreground',
          'oval', 'rectangular', 'rounded', 'thin', 'thick', 'large', 'small',
          'mix', 'parts', 'blend', 'smooth', 'vibrant', 'bold', 'outlines'
        ];
        
        const genericIndicators = [
          'general', 'basic', 'simple', 'various', 'different', 'multiple', 'some', 'any',
          'welcome', 'fantastic', 'amazing', 'wonderful', 'magical', 'journey',
          'no colors yet', 'just getting ready', 'still just pencil'
        ];
        
        const specificSteps = data.tutorial.steps.filter(step => {
          const instruction = step.instruction.toLowerCase();
          const hasSpecific = specificIndicators.some(indicator => instruction.includes(indicator));
          const hasGeneric = genericIndicators.some(indicator => instruction.includes(indicator));
          return hasSpecific && !hasGeneric;
        });
        
        const specificityScore = (specificSteps.length / data.tutorial.steps.length) * 100;
        console.log(`📊 Tutorial specificity score: ${specificityScore.toFixed(1)}%`);
        
        if (specificityScore < 50) {
          console.warn('⚠️ FALLBACK tutorial detected - vision model failed');
          console.warn('📝 Most specific step:', data.tutorial.steps[0]?.instruction);
        } else {
          console.log('✅ Vision-generated tutorial detected');
          console.log('📝 All Steps:');
          data.tutorial.steps.forEach((step, index) => {
            console.log(`Step ${index + 1}: ${step.title}`);
            console.log(`Instruction: ${step.instruction}`);
            console.log(`Tools: ${step.tools.join(', ')}`);
            console.log(`Colors: ${step.colors.join(', ')}\n`);
          });
          
          console.log('Color Palette:');
          data.tutorial.palette.forEach((color, index) => {
            console.log(`${index + 1}. ${color.name} (${color.hex}): ${color.description}`);
          });
        }
      }
      
      console.log('💾 Tutorial generated and will be saved to Firestore!');
      
      setTutorial(data.tutorial);
      setIsLoading(false); // Set loading to false after tutorial is set
      
      // Embed tutorial on the Ways of Seeing journey doc in userActivities (must be real Firestore doc id).
      try {
        let activityDocId = (reimagineActivityId && String(reimagineActivityId).trim())
          ? String(reimagineActivityId).trim()
          : '';
        if (!activityDocId && typeof artworkId === 'string' && artworkId.trim()) {
          const id = artworkId.trim();
          if (!/^https?:\/\//i.test(id) && !id.startsWith('data:')) {
            activityDocId = id;
          }
        }
        if (!activityDocId && originalImageUrl && typeof originalImageUrl === 'string') {
          activityDocId = (await findLatestWaysOfSeeingJourneyIdForArtwork(originalImageUrl.trim(), null, styleInfo?.id)) || '';
        }

        console.log('💾 StepByStepLearning: Saving tutorial onto userActivities doc:', {
          activityDocId: activityDocId || '(none)',
          styleId: styleInfo.id,
          styleName: styleInfo.name,
          hasTutorial: !!data.tutorial,
        });

        if (!activityDocId) {
          console.warn('⚠️ StepByStepLearning: No journey activity id — tutorial cannot attach to Magic Studio. Complete a transformation so a journey is saved, or open the tutorial from the dashboard.');
        } else {
          const saveResult = await saveTutorialWithId({
            originalImageUrl: transformedImage,
            transformedImageUrl: transformedImage,
            styleInfo: styleInfo,
            tutorial: data.tutorial,
            title: `Tutorial: ${styleInfo.name} Style`,
            description: `Step-by-step tutorial for creating ${styleInfo.name} artwork`,
            userId: userId || 'current-user',
            artworkId: activityDocId,
          });
          console.log('💾 StepByStepLearning: Save result:', saveResult);
          console.log('✅ Tutorial data merged onto journey activity');
        }
      } catch (activityError) {
        console.warn('⚠️ Failed to save tutorial activity:', activityError);
      }
    } catch (error) {
      console.error('❌ Error generating tutorial:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        type: error.constructor.name
      });
      
      // Show user-friendly error message for timeout or network errors
      if (error.message && (error.message.includes('timeout') || error.message.includes('timed out'))) {
        alert('Tutorial generation is taking longer than expected. Please try again or check your internet connection.');
      } else if (error.message && error.message.includes('No artwork image')) {
        alert('Error: No artwork image available. Please go back and select an artwork first.');
      } else {
        alert('Failed to generate tutorial. Please try again.');
      }
      
      // Fallback tutorial
      console.log('🔄 Using fallback tutorial due to error');
      setTutorial(generateFallbackTutorial());
    } finally {
      setIsLoading(false);
    }
  };

  const generateFallbackTutorial = () => ({
    title: `Create Your Own Amazing ${styleInfo.name} Masterpiece!`,
    materials: [
      "A big white paper (at least 11x14 inches) or canvas",
      "2 pencils: one regular #2 pencil and one soft 2B pencil for shading",
      "Acrylic paint set with primary colors (red, blue, yellow) and secondary colors (green, purple, orange)",
      "Brushes: big flat brush, medium round brush, small thin brush, tiny brush",
      "A white plastic palette or paper plate for mixing your colors",
      "A clean cup filled with water for washing your brushes",
      "Paper towels or old cloth for drying your brushes",
      "A comfortable table or desk with good lighting"
    ],
    color_guide: [
      {
        name: "Primary Colors",
        colors: ["red", "blue", "yellow"],
        description: "These are your basic colors - you can mix them to make other colors!"
      },
      {
        name: "Secondary Colors", 
        colors: ["green", "purple", "orange"],
        description: "Mix primary colors to make these: red + yellow = orange, blue + yellow = green, red + blue = purple"
      }
    ],
    steps: [
      {
        title: "Step 1: Set Up Your Amazing Artist Studio!",
        instruction: `Welcome artists. You'll need: a big white paper, pencils, paints with primary colors (red, blue, yellow) and secondary colors (green, purple, orange), brushes (big flat, medium round, small thin, tiny), palette, water cup, paper towels, and good lighting. When everything is ready, we can begin.`,
        tools: [
          "A big white paper (at least 11x14 inches) or canvas",
          "2 pencils: one regular #2 pencil and one soft 2B pencil for shading",
          "Acrylic paint set with primary colors (red, blue, yellow) and secondary colors (green, purple, orange)",
          "Brushes: big flat brush, medium round brush, small thin brush, tiny brush",
          "A white plastic palette or paper plate for mixing your colors",
          "A clean cup filled with water for washing your brushes",
          "Paper towels or old cloth for drying your brushes",
          "A comfortable table or desk with good lighting"
        ],
        colors: ["No colors yet - just getting ready!"],
        time_minutes: 5,
        overlay: null
      },
      {
        title: "Step 2: Draw the Big Shapes First",
        instruction: "Now let's start creating! Take your regular #2 pencil and draw the biggest shapes you see in the artwork. Look at the center - what's the main thing you see? Draw that first! Then look at the background - what big shapes are there? Draw them too! Don't worry about details yet - just big, simple shapes like circles, squares, or triangles. Make your lines light so you can erase them later if needed.",
        tools: ["Regular #2 pencil", "Eraser"],
        colors: ["No colors yet - just pencil lines"],
        time_minutes: 8,
        overlay: null
      },
      {
        title: "Step 3: Add the Medium Shapes",
        instruction: "Great job! Now look at the artwork again. What medium-sized shapes do you see? Draw these shapes inside your big shapes. Keep your lines light and simple. Think of it like building with blocks - you're adding the medium blocks to your big blocks. Look at the top, bottom, left, and right areas - what do you see there?",
        tools: ["Regular pencil", "Eraser"],
        colors: ["Still just pencil - we're building the foundation!"],
        time_minutes: 10,
        overlay: null
      },
      {
        title: "Step 4: Add the Small Details",
        instruction: "Now it's time for the fun details! Look carefully at the artwork. What small things do you see? Draw these details inside your medium shapes. Look at the corners and edges - what special details are there? Take your time and be patient - details make your artwork special!",
        tools: ["Regular pencil", "Soft 2B pencil", "Eraser"],
        colors: ["Pencil details - almost ready for color!"],
        time_minutes: 12,
        overlay: null
      },
      {
        title: "Step 5: Paint the Background First",
        instruction: "Now for the colors! Start with your big flat brush and paint the background first. Look at the artwork - what color is the background? Is it blue like the sky, green like grass, or another color? If you need to mix colors, remember: red + yellow = orange, blue + yellow = green, red + blue = purple! Paint the whole background area with broad, even strokes.",
        tools: ["Big flat brush", "Background color paint", "Water cup"],
        colors: ["Your main background color"],
        time_minutes: 8,
        overlay: null
      },
      {
        title: "Step 6: Paint the Big Objects",
        instruction: "Fantastic! Now paint the biggest objects in your artwork. Look at the center - what color are the main objects? Use your medium round brush and paint each big shape with its color. If you need to mix colors, try different combinations! Take your time and try to stay inside the lines you drew. If you go outside the lines a little, that's okay - we can fix it!",
        tools: ["Medium round brush", "Object colors", "Water cup"],
        colors: ["The main colors of your big objects"],
        time_minutes: 15,
        overlay: null
      },
      {
        title: "Step 7: Add the Medium Objects",
        instruction: "Wonderful! Now paint the medium-sized objects. Look at the artwork - what colors are the medium objects? Maybe they're different colors than the big objects. Use your medium round brush and paint each medium shape carefully. Look at the top, bottom, left, and right areas - what colors do you see there? Think about how the colors look next to each other - do they look good together?",
        tools: ["Medium round brush", "Medium object colors", "Water cup"],
        colors: ["Colors for your medium objects"],
        time_minutes: 12,
        overlay: null
      },
      {
        title: "Step 8: Paint the Small Details",
        instruction: "This is where your artwork really comes to life! Use your small thin brush and paint all the little details. Look at the artwork - what colors are the small things? Windows, doors, flowers, decorations? Paint each small detail with care. Look at the corners and edges - what special details need color? This step takes patience, but it's worth it!",
        tools: ["Small thin brush", "Detail colors", "Water cup"],
        colors: ["Colors for all your small details"],
        time_minutes: 15,
        overlay: null
      },
      {
        title: "Step 9: Add Highlights and Shadows",
        instruction: "Now let's make your artwork look magical! Look at the artwork - where is the light coming from? Where are the bright spots (highlights) and dark spots (shadows)? Use your small thin brush with white paint for highlights and black or dark paint for shadows. Add a little white to any color to make it lighter, or add black to make it darker. This makes your artwork look like it's popping off the page!",
        tools: ["Small thin brush", "White paint", "Black or dark paint", "Water cup"],
        colors: ["White for highlights", "Black or dark colors for shadows"],
        time_minutes: 10,
        overlay: null
      },
      {
        title: "Step 10: Final Touches and Sign Your Name!",
        instruction: "You're almost done! Look at your artwork and the original. Is there anything you want to add or change? Maybe add some sparkles, dots, or special details. Look at the corners and edges - are there any final touches needed? When you're happy with your artwork, sign your name in the corner with pride - you created this! Take a step back and admire your masterpiece! You're becoming a real ${styleInfo.name} artist!",
        tools: ["Any brush size", "Any colors you want", "Your signature pen or pencil"],
        colors: ["Any final colors you want to add"],
        time_minutes: 8,
        overlay: null
      }
    ]
  });

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      if (isPlaying) {
        speechSynthesis.cancel();
        setIsPlaying(false);
      } else {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.8;
        utterance.pitch = 1.1;
        utterance.onend = () => setIsPlaying(false);
        speechSynthesis.speak(utterance);
        setIsPlaying(true);
      }
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      console.log('📤 Starting tutorial artwork upload...');
      console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
      console.log('Style info:', styleInfo);
      console.log('User ID:', userId);
      console.log('Transformed image:', transformedImage?.substring(0, 100) + '...');

      // Check if user is logged in
      if (!userId || userId === 'current-user') {
        alert('Please log in to upload your artwork!');
        return;
      }

      // Validate file
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPEG, PNG, etc.)');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB. Please select a smaller image.');
        return;
      }

      const formData = new FormData();
      formData.append('artwork', file);

      // Use the true original artwork context (prefer prop if provided)
      const originalArtworkUrl = (typeof originalImageUrl === 'string' && originalImageUrl)
        ? originalImageUrl
        : (artworkId || transformedImage || '');
      formData.append('originalArtworkUrl', originalArtworkUrl);
      formData.append('styleId', styleInfo.id || styleInfo.name.toLowerCase().replace(/\s+/g, ''));
      formData.append('styleName', styleInfo.name);
      formData.append('userId', userId || 'current-user');
      formData.append('title', `My ${styleInfo.name} Creation`);
      formData.append('description', `Artwork created following the ${styleInfo.name} tutorial`);

      // For localhost, API_ENDPOINTS.backendBase is http://localhost:3001 (no /api), so we need to add /api
      // For Firebase Functions, API_ENDPOINTS.backendBase already includes /api
      const apiPath = API_ENDPOINTS.backendBase.includes('/api') ? '/tutorial-tag/upload' : '/api/tutorial-tag/upload';
      const uploadUrl = `${API_ENDPOINTS.backendBase}${apiPath}`;
      console.log('📤 Sending upload request to:', uploadUrl);

      // Add timeout to prevent hanging (60 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('⏱️ Upload timeout - aborting request after 60 seconds');
        controller.abort();
      }, 60000);

      let response;
      try {
        // Don't set Content-Type header - browser will set it with boundary for FormData
        // Don't include headers object at all - let browser set it automatically
        response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        console.log('📥 Upload response status:', response.status);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Upload request timed out after 60 seconds. Please try again.');
        }
        throw fetchError;
      }

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Upload successful:', data);
        setShowUpload(false);
        console.log('✅ Upload complete - artwork will be visible in Ways of Seeing expand section');
        try {
          // Also save to userActivities so Ways of Seeing can display it
          // data.data.imageUrl should already be a full URL or relative path
          let uploadedImageUrl = data?.data?.imageUrl || '';
          // If it's a relative path, prepend backend base
          if (uploadedImageUrl && !uploadedImageUrl.startsWith('http') && !uploadedImageUrl.startsWith('/')) {
            uploadedImageUrl = `${API_ENDPOINTS.backendBase}/${uploadedImageUrl}`;
          } else if (uploadedImageUrl && uploadedImageUrl.startsWith('/')) {
            uploadedImageUrl = `${API_ENDPOINTS.backendBase}${uploadedImageUrl}`;
          }
          console.log('💾 Saving tutorial activity to userActivities for Ways of Seeing:', {
            originalImageUrl: originalArtworkUrl?.substring(0, 100),
            transformedImageUrl: uploadedImageUrl?.substring(0, 100),
            styleId: styleInfo.id,
            styleName: styleInfo.name
          });
          try {
            const activityId = await saveTutorialActivity({
              originalImageUrl: originalArtworkUrl,
              transformedImageUrl: uploadedImageUrl,
              styleInfo: styleInfo,
              tutorial: null,
              originalArtworkId: null,
              title: `My ${styleInfo.name} Creation`,
              description: `Artwork created following the ${styleInfo.name} tutorial`
            }, true);
            console.log('✅ Tutorial activity saved to userActivities with ID:', activityId);
            console.log('✅ Artwork will now appear in Ways of Seeing for this original artwork');
          } catch (e) {
            console.error('❌ Could not save tutorial upload to userActivities:', e?.message);
            console.error('❌ Error details:', e);
          }
          
          // Ask whether to share publicly in the Artist Salon (two-step modal: success, then share)
          let sharedPublicly = false;
          try {
            const shouldShare = await askSharePublicly();
            sharedPublicly = !!shouldShare;
            if (shouldShare) {
              // Avoid duplicates: if backend already created a tutorialArtworks doc for this upload,
              // just set visibility to public; else create one.
              const relativePath = data?.data?.imageUrl || '';
              const absolutePath = `${API_ENDPOINTS.backendBase}${relativePath}`;
              let existing = null;
              try {
                const q1 = query(collection(db, 'tutorialArtworks'), where('userId', '==', userId), where('imageUrl', '==', relativePath));
                const snap1 = await getDocs(q1);
                if (!snap1.empty) existing = { id: snap1.docs[0].id };
              } catch (_) {}
              if (!existing) {
                try {
                  const q2 = query(collection(db, 'tutorialArtworks'), where('userId', '==', userId), where('imageUrl', '==', absolutePath));
                  const snap2 = await getDocs(q2);
                  if (!snap2.empty) existing = { id: snap2.docs[0].id };
                } catch (_) {}
              }

              if (existing) {
                await updateDoc(doc(db, 'tutorialArtworks', existing.id), { visibility: 'public', updatedAt: serverTimestamp() });
              } else {
                await addDoc(collection(db, 'tutorialArtworks'), {
                  userId,
                  originalArtworkUrl,
                  imageUrl: relativePath,
                  styleId: styleInfo.id || styleInfo.name?.toLowerCase?.().replace(/\s+/g, ''),
                  styleName: styleInfo.name,
                  title: `My ${styleInfo.name} Creation`,
                  description: `Artwork created following the ${styleInfo.name} tutorial`,
                  visibility: 'public',
                  createdAt: serverTimestamp()
                });
              }
              // Success line is shown in the share modal (step 1: Uploaded successfully)
            }
          } catch (shareErr) {
            console.warn('Could not save public tutorial artwork:', shareErr?.message);
          }

          // Navigate to Ways of Seeing for this artwork (always use original artwork context)
          navigate('/artist-wall', { state: { openWaysOfSeeing: true, artwork: originalArtworkUrl, title: styleInfo?.name || '' } });
        } catch (_) {}
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          const errorText = await response.text();
          errorData = { error: `Server error (${response.status}): ${response.statusText}`, details: errorText };
        }
        console.error('❌ Tutorial tag upload failed:', errorData);
        showArtmosphereToast(`Upload failed: ${errorData.error || errorData.details || 'Unknown error'}`);
        setShowUpload(false);
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      showArtmosphereToast(`Upload failed: ${error.message || 'Please try again.'}`);
      setShowUpload(false);
    } finally {
      // Ensure upload modal is closed even if there's an error
      // setShowUpload(false); // Already set in error handlers above
    }
  };



  // Image resizing function to reduce payload size
  const resizeImage = (dataUrl, maxSize) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // Add timeout to prevent hanging on very large images (10 seconds)
      const timeoutId = setTimeout(() => {
        reject(new Error('Image resize timeout - image too large or corrupted'));
      }, 10000);
      
      img.onload = () => {
        clearTimeout(timeoutId);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions
          let { width, height } = img;
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw resized image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to data URL with reduced quality
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(resizedDataUrl);
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        clearTimeout(timeoutId);
        reject(new Error('Failed to load image for resizing'));
      };
      
      img.src = dataUrl;
    });
  };

  // Debug logging
  console.log('🎨 StepByStepLearning render:', {
    isLoading,
    hasTutorial: !!tutorial,
    tutorialTitle: tutorial?.title,
    tutorialSteps: tutorial?.steps?.length,
    currentStep,
    transformedImage: transformedImage?.substring(0, 50) + '...'
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-4 z-[100000002] backdrop-blur-xl bg-white/10">
        <UpgradeQuotaModal
          open={showUpgradeModal}
          onClose={() => {
            dismissUpgradeModal();
            onClose?.();
          }}
          feature={upgradeModalFeature}
        />
        {!showUpgradeModal && (
          <div className="bg-gradient-to-br from-amber-50/95 to-amber-100/50 rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-2 border-amber-200/80 w-full max-w-md ring-1 ring-amber-100/50">
            <svg className="animate-spin h-12 w-12 text-amber-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <h3 className="text-xl font-bold text-zinc-800 mb-2 text-center tracking-tight">
              One moment, fellow artist
            </h3>
            <p className="text-sm text-zinc-600 text-center leading-relaxed">
              ARTmosphere is curating an art-style tutorial for your artwork.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 md:p-4" style={{ zIndex: 100000002 }}>
      <UpgradeQuotaModal
        open={showUpgradeModal}
        onClose={() => {
          dismissUpgradeModal();
          // Return the user to the screen where tutorial generation was triggered.
          onClose?.();
        }}
        feature={upgradeModalFeature}
      />
      {!showUpgradeModal && (
      <div className="bg-gradient-to-br from-amber-50/95 via-amber-50/70 to-amber-100/45 rounded-none md:rounded-3xl p-3 md:p-6 max-w-6xl w-full h-screen md:h-auto md:max-h-[90vh] flex flex-col overflow-hidden relative border-2 border-amber-200/80 ring-1 ring-amber-100/50 shadow-[0_28px_70px_-20px_rgba(146,64,14,0.28)]" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh', 
        maxHeight: '100vh',
        paddingTop: typeof window !== 'undefined' && window.innerWidth < 768 
          ? 'max(2rem, calc(env(safe-area-inset-top, 0px) + 2rem))' 
          : undefined,
        paddingLeft: typeof window !== 'undefined' && window.innerWidth < 768 
          ? 'max(0.75rem, calc(env(safe-area-inset-left, 0px) + 0.75rem))' 
          : undefined,
        paddingRight: typeof window !== 'undefined' && window.innerWidth < 768 
          ? 'max(0.75rem, calc(env(safe-area-inset-right, 0px) + 0.75rem))' 
          : undefined
      }}>
        {/* Close — flush to outer modal corner (not inline with header actions) */}
        <button 
          type="button"
          onClick={onClose} 
          className="absolute z-[60] flex items-center justify-center bg-amber-50/95 hover:bg-amber-50 shadow-md hover:shadow-lg transition-all border-l border-b border-amber-200/80 rounded-bl-2xl md:rounded-bl-3xl p-2.5 md:p-3 min-w-[2.75rem] min-h-[2.75rem] md:min-w-[3rem] md:min-h-[3rem]"
          style={{
            top: 'max(0px, env(safe-area-inset-top, 0px))',
            right: 'max(0px, env(safe-area-inset-right, 0px))',
          }}
          title="Close"
        >
          <FaTimes className="w-3.5 h-3.5 md:w-5 md:h-5 text-zinc-700" />
        </button>
        {/* After upload: success step, then Artist Salon share prompt */}
        {sharePromptOpen && (
          <div className="fixed inset-0 z-[100000003] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => {
                setSharePromptOpen(false);
                setSharePromptPhase('uploaded');
                sharePromptResolverRef.current?.(false);
                sharePromptResolverRef.current = null;
              }}
            />
            <div className="relative bg-[#fffdf9]/98 backdrop-blur-md border border-[#e8d4bc] rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-stone-200 to-rose-100 text-[#2F251B] font-bold shadow-sm mb-3 border border-[#e8d4bc]">
                  A
                </div>
                {sharePromptPhase === 'uploaded' ? (
                  <>
                    <h3 className="text-xl font-bold text-[#2F251B] mb-2">Uploaded successfully</h3>
                    <p className="text-[#5c4a3d] mb-6 text-sm leading-relaxed">
                      Your tutorial artwork is saved. Next, you can choose whether to share it in the Artist Salon for others to learn from.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSharePromptPhase('share')}
                      className="w-full px-4 py-2.5 rounded-full text-white bg-[#5c4a3d] hover:bg-[#4a3b32] shadow-md transition-colors"
                    >
                      Continue
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-[#2F251B] mb-2">Share in the Artist Salon?</h3>
                    <p className="text-[#5c4a3d] mb-5 text-sm leading-relaxed">
                      Let other artists learn from your creation by sharing it publicly in the Artist Salon.
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setSharePromptOpen(false);
                          setSharePromptPhase('uploaded');
                          sharePromptResolverRef.current?.(false);
                          sharePromptResolverRef.current = null;
                        }}
                        className="flex-1 px-4 py-2 rounded-full border border-[#d4c4b0] text-[#5c4a3d] bg-white hover:bg-[#faf6f0]"
                      >
                        Not now
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSharePromptOpen(false);
                          setSharePromptPhase('uploaded');
                          sharePromptResolverRef.current?.(true);
                          sharePromptResolverRef.current = null;
                        }}
                        className="flex-1 px-4 py-2 rounded-full text-white bg-[#5c4a3d] hover:bg-[#4a3b32] shadow-md transition-colors"
                      >
                        Share
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 md:mb-6 gap-2 md:gap-0 flex-shrink-0 rounded-2xl border-2 border-amber-200/70 bg-gradient-to-r from-amber-50/90 to-amber-100/40 backdrop-blur-sm px-3 py-2 md:px-4 md:py-3 ring-1 ring-amber-100/50 shadow-sm">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm md:text-2xl font-bold text-zinc-800">
              {isLoading ? 'Something fun is on the way…' : tutorial?.title}
            </h2>
            <p className="text-[10px] md:text-base text-zinc-600 hidden md:block">
              {isLoading ? 'Your artwork is about to get its own little surprises — stay tuned!' : 'Let\'s create your own masterpiece together!'}
            </p>
          </div>
          <div className="flex items-center gap-1 md:gap-3">
            {/* Back to Results Button - Only show when tutorial steps are visible */}
            {!isLoading && tutorial && hasStarted && (
              <button
                onClick={() => {
                  setHasStarted(false); // Reset to show "Things to Remember" section
                  setCurrentStep(0); // Reset to first step
                }}
                className="px-1.5 md:px-4 py-0.5 md:py-2 rounded-full text-[10px] md:text-base font-semibold transition-all duration-300 bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-md hover:from-amber-600 hover:to-amber-700 hover:shadow-lg"
                style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
              >
                <span className="hidden md:inline">Back to Style Info</span>
                <span className="md:hidden">Back</span>
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="fixed inset-0 z-[9999] p-4 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(251,191,36,0.22),transparent_42%),radial-gradient(circle_at_85%_75%,rgba(244,114,182,0.14),transparent_36%)]" />

            <div className="relative bg-amber-50/95 backdrop-blur-sm border-2 border-amber-200/80 rounded-3xl shadow-2xl max-w-md w-full mx-auto p-6 text-center ring-1 ring-amber-100/50">
              <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-gradient-to-br from-amber-200 to-amber-100 flex items-center justify-center shadow-inner border border-amber-200/60">
                <div className="h-6 w-6 rounded-full border-2 border-amber-600/30 border-t-amber-600 animate-spin" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-zinc-800 mb-2 tracking-tight">
                One moment, fellow artist
              </h3>
              <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                ARTmosphere is curating an art-style tutorial for your artwork.
              </p>
            </div>
          </div>
        )}

        {/* Main Content - Only show when not loading and tutorial exists */}
        {!isLoading && tutorial && (
          /* Sophisticated split layout: artwork left, content right */
          <div className="flex-1 overflow-y-auto min-h-0" style={{ 
            flex: '1 1 0%', 
            minHeight: 0, 
            overflowY: 'auto', 
            overflowX: 'visible', 
            WebkitOverflowScrolling: 'touch', 
            height: 'auto', 
            maxHeight: 'none',
            paddingTop: typeof window !== 'undefined' && window.innerWidth < 768 ? '0.5rem' : undefined,
            paddingBottom: typeof window !== 'undefined' && window.innerWidth < 768 
              ? 'max(1rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))' 
              : undefined
          }}>
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.1fr)] gap-4 md:gap-6 px-1 md:px-2 xl:h-full">
              <div className="self-start xl:self-stretch xl:h-full">
                <div className="rounded-2xl border-2 border-amber-200/80 bg-gradient-to-b from-amber-50/90 via-amber-50/50 to-amber-100/35 p-2 md:p-3 shadow-lg ring-1 ring-amber-100/45 xl:h-full flex flex-col">
                  <div ref={artworkImageRef} className="relative w-full rounded-xl border border-amber-200/70 overflow-hidden h-[220px] md:h-[420px] xl:h-full xl:min-h-[520px] bg-white/70">
                    {transformedImage ? (
                      <>
                        <img 
                          src={(() => {
                            let imgUrl = transformedImage;
                            if (imgUrl && typeof imgUrl === 'string') {
                              if (imgUrl.startsWith('/uploads') || imgUrl.startsWith('uploads')) {
                                const backendBase = API_ENDPOINTS.backendBase || '';
                                imgUrl = imgUrl.startsWith('/uploads') ? `${backendBase}${imgUrl}` : `${backendBase}/${imgUrl}`;
                              }
                            }
                            return imgUrl;
                          })()} 
                          alt={`${styleInfo.name} artwork`} 
                          className="w-full h-full object-contain relative bg-white"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {/* Grid Overlay */}
                        <GridOverlay 
                          containerRef={artworkImageRef}
                          enabled={showGrid}
                          gridSize={gridSize}
                        />
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <p className="text-gray-500 text-xs md:text-base">Artwork not available</p>
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3 text-center text-zinc-700 font-semibold bg-amber-50/95 px-2 py-1 md:px-3 md:py-2 rounded text-xs md:text-sm z-10 border border-amber-200/70 shadow-sm">
                      {styleInfo.name} Style
                    </div>
                    {/* Grid Controls - Top Left */}
                    <div ref={gridControlsRef} className="absolute top-2 left-2 md:top-3 md:left-3 z-30 flex flex-col gap-1.5 md:gap-2">
                      {/* Toggle Grid Button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newShowGrid = !showGrid;
                          setShowGrid(newShowGrid);
                          if (newShowGrid) {
                            setShowGridSizeSelector(true);
                          } else {
                            setShowGridSizeSelector(false);
                          }
                        }}
                        className={`px-2 py-2 md:px-3 md:py-2 rounded-lg shadow-md transition-all duration-200 border-2 ${
                          showGrid
                            ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white border-amber-400'
                            : 'bg-white/95 hover:bg-amber-50/90 text-zinc-700 border-amber-200/80'
                        }`}
                        title={showGrid ? 'Hide Grid' : 'Show Grid'}
                        style={{ minWidth: '36px', minHeight: '36px' }}
                      >
                        <svg className="w-3.5 h-3.5 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      </button>
                      
                      {/* Grid Size Selector */}
                      {showGrid && showGridSizeSelector && (
                        <div ref={gridSizeSelectorRef} className="bg-amber-50/95 backdrop-blur-sm rounded-lg shadow-lg border border-amber-200/80 p-1.5 md:p-2 flex flex-col gap-0.5 md:gap-1">
                          <div className="text-[10px] md:text-xs font-semibold text-zinc-700 mb-0.5 md:mb-1">Grid:</div>
                          {[3, 4, 6].map((size) => (
                            <button
                              key={size}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setGridSize(size);
                                setShowGridSizeSelector(false);
                              }}
                              className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded text-[10px] md:text-xs font-medium transition-all ${
                                gridSize === size
                                  ? 'bg-amber-600 text-white shadow-sm'
                                  : 'bg-white/90 text-zinc-700 hover:bg-amber-100/80 border border-amber-200/70'
                              }`}
                            >
                              {size}×{size}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="absolute top-2 right-2 md:top-3 md:right-3 bg-amber-50/95 rounded-full p-1 md:p-2 shadow z-20 hover:bg-amber-100/90 transition-colors border border-amber-200/70 text-zinc-700"
                      onClick={() => setImageExpanded(true)}
                      title="Expand"
                    >
                      <FaExpandArrowsAlt className="w-3 h-3 md:w-4 md:h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content pane */}
              <div className="space-y-3 md:space-y-4 pb-4 md:pb-6 rounded-2xl border-2 border-amber-200/80 bg-gradient-to-br from-amber-50/90 via-amber-50/50 to-amber-100/35 backdrop-blur-sm p-3 md:p-4 shadow-lg ring-1 ring-amber-100/45 xl:h-full xl:flex xl:flex-col" style={{ overflow: 'visible', height: 'auto', maxHeight: 'none', minHeight: 'auto' }}>
              {/* Style Introduction and Reminders (show until user starts) */}
              {!hasStarted && tutorial?.style_introduction && (
                <div className="style-description-container bg-amber-50/60 backdrop-blur-sm rounded-xl p-3 md:p-4 shadow-sm mb-3 md:mb-4 border-2 border-amber-200/60 ring-1 ring-amber-100/40" style={{ overflow: 'visible', height: 'auto', maxHeight: 'none', minHeight: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-zinc-800 text-sm md:text-base">
                    About {styleInfo.name} Style:
                  </h4>
                  <p className="style-description-text text-xs md:text-sm leading-relaxed text-zinc-700" style={{ overflow: 'visible', height: 'auto', maxHeight: 'none', minHeight: 'auto', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word', textOverflow: 'clip', display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box', hyphens: 'auto' }}>
                    {tutorial.style_introduction}
                  </p>
                </div>
              )}

              {!hasStarted && tutorial?.style_reminders && (
                <div className="style-description-container bg-amber-50/60 backdrop-blur-sm rounded-xl p-3 md:p-4 shadow-sm mb-3 md:mb-4 border-2 border-amber-200/60 ring-1 ring-amber-100/40">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-zinc-800 text-sm md:text-base">
                    Things to Remember:
                  </h4>
                  <p className="style-description-text text-xs md:text-sm leading-relaxed text-zinc-700">
                    {tutorial.style_reminders}
                  </p>
                </div>
              )}

              {/* Start button to reveal flashcards */}
              {!hasStarted && (
                <div className="flex justify-center mb-3 md:mb-4">
                  <button
                    onClick={() => setHasStarted(true)}
                    className="px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-md hover:shadow-lg text-xs md:text-base font-semibold transition-all"
                  >
                    Let's start {styleInfo.name} creation
                  </button>
                </div>
              )}
              
              {/* Step Navigation */}
              {hasStarted && (
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <button 
                  onClick={() => {
                    if (currentStep === 10) {
                      // If we're on Step 11, go back to Step 10
                      setCurrentStep(9);
                      setShowStep11(false);
                    } else {
                      setCurrentStep(Math.max(0, currentStep - 1));
                    }
                  }}
                  disabled={currentStep === 0}
                  className={`p-2 rounded-full transition-all ${
                    currentStep === 0
                      ? 'bg-amber-100 text-amber-300 cursor-not-allowed border border-amber-200/50'
                      : 'bg-gradient-to-br from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-md border border-amber-300/40'
                  }`}
                >
                  ←
                </button>
                
                <div className="text-center">
                  <span className="text-zinc-700 font-semibold text-xs md:text-sm lg:text-base">
                    Step {currentStep + 1} of {tutorial?.steps.length + 1}
                  </span>
                  <div className="flex gap-1 md:gap-1.5 mt-1 md:mt-2 justify-center">
                    {tutorial?.steps.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all ${
                          index === currentStep ? 'bg-amber-600' : 'bg-amber-200/80'
                        }`}
                      />
                    ))}
                    {/* Step 11 indicator */}
                    <div
                      className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all ${
                        currentStep === 10 ? 'bg-amber-600' : 'bg-amber-200/80'
                      }`}
                    />
                  </div>
                </div>
                
                <button 
                  onClick={() => {
                    if (currentStep === 9) {
                      // If we're on Step 10, go to Step 11
                      setCurrentStep(10);
                      setShowStep11(true);
                    } else if (currentStep < 9) {
                      setCurrentStep(Math.min(tutorial?.steps.length - 1, currentStep + 1));
                    }
                  }}
                  disabled={currentStep === 10}
                  className={`p-2 rounded-full transition-all ${
                    currentStep === 10
                      ? 'bg-amber-100 text-amber-300 cursor-not-allowed border border-amber-200/50'
                      : 'bg-gradient-to-br from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-md border border-amber-300/40'
                  }`}
                >
                  →
                </button>
              </div>
              )}
              
              {/* Flashcard Stack */}
              {hasStarted && (
              <div className="relative min-h-[300px] md:min-h-[400px] xl:min-h-0 xl:flex-1">
                {tutorial?.steps.map((step, index) => (
                  <FlashCard
                    key={index}
                    step={step}
                    stepNumber={index + 1}
                    isActive={currentStep === index && !(currentStep === 10 && showStep11)}
                    speakText={speakText}
                    isPlaying={isPlaying}
                    transformedImage={transformedImage}
                    originalImageUrl={originalImageUrl}
                    totalSteps={tutorial?.steps.length + 1}
                    onStep11Trigger={() => setShowStep11(true)}
                    onShowUpload={() => setShowUpload(true)}
                  />
                ))}
                
                {/* Step 11: Share Your Creation - Only show when triggered */}
                {showStep11 && (
                  <FlashCard
                    step={{
                      title: `Step 11: You're Now a ${styleInfo.name} Artist!`,
                      instruction: `Congratulations! You've just created your own ${styleInfo.name} masterpiece! You are now officially a ${styleInfo.name} artist! Let's share your amazing creation with the world. Upload your artwork - we can't wait to see what you've created!`,
                      tools: ["Your completed artwork"],
                      colors: ["Your masterpiece colors!"],
                      time_minutes: 5
                    }}
                    stepNumber={11}
                    isActive={showStep11 && currentStep === 10}
                    speakText={speakText}
                    speakPayload={`Congratulations! You've just created your own ${styleInfo.name} masterpiece! You are now officially a ${styleInfo.name} artist!`}
                    isPlaying={isPlaying}
                    transformedImage={transformedImage}
                    originalImageUrl={originalImageUrl}
                    totalSteps={tutorial?.steps.length + 1}
                    onStep11Trigger={() => {}}
                    isStep11={true}
                    onShowUpload={() => setShowUpload(true)}
                  />
                )}
              </div>
              )}
              </div>
            </div>
            </div>
        )}

        {/* File Upload Modal */}
        {showUpload && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 100000002 }}>
            <div className="bg-gradient-to-br from-amber-50/95 to-amber-100/40 rounded-xl p-6 max-w-md w-full mx-4 border-2 border-amber-200/80 ring-1 ring-amber-100/50 shadow-xl">
              <h3 className="text-lg font-bold text-zinc-800 mb-4">Upload Your Artwork</h3>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="w-full p-2 border border-amber-200/80 rounded-lg bg-amber-50/40 text-zinc-700"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setShowUpload(false)}
                  className="flex-1 border-2 border-amber-200/80 text-zinc-700 hover:bg-amber-100/50 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen Modal for Expanded Image */}
        {imageExpanded && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" style={{
            paddingTop: typeof window !== 'undefined' && window.innerWidth < 768 
              ? 'max(1rem, calc(env(safe-area-inset-top, 0px) + 1rem))' 
              : undefined,
            paddingBottom: typeof window !== 'undefined' && window.innerWidth < 768 
              ? 'max(1rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))' 
              : undefined,
            paddingLeft: typeof window !== 'undefined' && window.innerWidth < 768 
              ? 'max(0.75rem, calc(env(safe-area-inset-left, 0px) + 0.75rem))' 
              : undefined,
            paddingRight: typeof window !== 'undefined' && window.innerWidth < 768 
              ? 'max(0.75rem, calc(env(safe-area-inset-right, 0px) + 0.75rem))' 
              : undefined
          }}>
            <button
              className="absolute top-2 right-2 md:top-4 md:right-4 bg-white/95 hover:bg-white rounded-full p-2 md:p-3 shadow-lg hover:shadow-xl transition-all z-60 border-2 border-amber-200/50"
              onClick={() => setImageExpanded(false)}
              title="Close"
              style={{
                top: typeof window !== 'undefined' && window.innerWidth < 768 
                  ? 'max(0.5rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))' 
                  : undefined,
                right: typeof window !== 'undefined' && window.innerWidth < 768 
                  ? 'max(0.5rem, calc(env(safe-area-inset-right, 0px) + 0.5rem))' 
                  : undefined
              }}
            >
              <FaTimes className="w-4 h-4 md:w-5 md:h-5 text-zinc-700" />
            </button>
            {transformedImage ? (
              <div ref={expandedArtworkRef} className="relative max-w-full max-h-[90vh]">
                <img
                  src={transformedImage}
                  alt="Expanded Artwork"
                  className="max-w-full max-h-[90vh] rounded-2xl border-4 border-amber-200 shadow-2xl"
                  style={{ background: 'white' }}
                />
                {/* Grid Overlay on Expanded View */}
                {showGrid && (
                  <GridOverlay 
                    containerRef={expandedArtworkRef}
                    enabled={showGrid}
                    gridSize={gridSize}
                  />
                )}
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-2xl">
                <p className="text-gray-500">Transformed artwork not available</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

/**
 * Split tutorial instruction into main sub-steps for kid-friendly accordions.
 * Handles numbered blocks, paragraph breaks, markdown bullets, or sentence groups.
 */
function splitIntoSentences(text) {
  if (!text || typeof text !== 'string') return [];
  const s = text.replace(/\s+/g, ' ').trim();
  if (!s) return [];
  const parts = s.split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/);
  return parts.map((p) => p.trim()).filter(Boolean);
}

function splitBodyBullets(body) {
  if (!body || typeof body !== 'string') return [];
  const trimmed = body.trim();
  if (trimmed.includes('\n')) {
    return trimmed
      .split(/\r?\n/)
      .map((l) => l.trim().replace(/^[-•*]\s+/, ''))
      .filter(Boolean);
  }
  const sents = splitIntoSentences(trimmed);
  return sents.length ? sents : [trimmed];
}

function parseInstructionSubsteps(instruction, opts = {}) {
  const t = (instruction || '').trim();
  if (!t) return [];

  const earlySteps = !!opts.earlySteps;
  const sentenceGroup = earlySteps ? 1 : opts.sentenceGroupSize ?? 2;

  const make = (id, label, bullets) => ({
    id,
    label: label.length > 96 ? `${label.slice(0, 93)}…` : label,
    bullets: bullets.map((b) => b.trim()).filter(Boolean),
  });

  // Numbered blocks starting a line: "1. ..." "\n2. ..."
  if (/\n\s*\d+[\).\s]/.test(t) || /^\d+[\).\s]/.test(t)) {
    const parts = t.split(/\n\s*(?=\d+[\).\s])/).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1 || (parts.length === 1 && /^\d+[\).\s]/.test(parts[0]))) {
      return parts.map((part, i) => {
        const m = part.match(/^\d+[\).\s]+\s*([\s\S]*)$/);
        const body = (m ? m[1] : part).trim();
        const bullets = splitBodyBullets(body);
        const label = bullets[0] || `Step ${i + 1}`;
        return make(`num-${i}`, label, bullets);
      });
    }
  }

  // Double-newline paragraphs
  const paras = t.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paras.length > 1) {
    return paras.map((p, i) => {
      const bullets = splitBodyBullets(p);
      const label = bullets[0] || `Part ${i + 1}`;
      return make(`para-${i}`, label, bullets);
    });
  }

  // Lines that are mostly bullet lines
  const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const bulletLines = lines.filter((l) => /^[-•*]\s+/.test(l));
  if (lines.length >= 2 && bulletLines.length >= Math.ceil(lines.length * 0.5)) {
    return bulletLines.map((line, i) => {
      const inner = line.replace(/^[-•*]\s+/, '').trim();
      const bullets = splitBodyBullets(inner);
      const label = bullets[0] || `Tip ${i + 1}`;
      return make(`bullet-${i}`, label, bullets);
    });
  }

  // Single paragraph: group sentences (smaller groups on early steps 1–2)
  const sents = splitIntoSentences(t);
  if (sents.length <= 1) {
    const bullets = sents.length ? sents : [t];
    const one = [make('single', bullets[0].length > 96 ? `${bullets[0].slice(0, 93)}…` : bullets[0], bullets)];
    if (earlySteps && bullets[0] && bullets[0].length > 90) {
      const finer = splitIntoSentences(bullets[0]);
      if (finer.length > 1) {
        return finer.map((sentence, i) =>
          make(`early-${i}`, sentence.length > 96 ? `${sentence.slice(0, 93)}…` : sentence, [sentence])
        );
      }
    }
    return one;
  }
  const GROUP = sentenceGroup;
  const out = [];
  for (let i = 0; i < sents.length; i += GROUP) {
    const grp = sents.slice(i, i + GROUP);
    const label = grp[0];
    out.push(make(`grp-${i}`, label, grp));
  }
  if (earlySteps && out.length === 1 && out[0].bullets.length > 1) {
    return out[0].bullets.map((b, i) => make(`eb-${i}`, b.length > 90 ? `${b.slice(0, 87)}…` : b, [b]));
  }
  return out;
}

function fanOutMultiBulletsForEarlySteps(list, stepNumber, isStep11) {
  if (isStep11 || stepNumber > 2 || !list?.length) return list;
  if (list.length !== 1 || list[0].bullets.length <= 1) return list;
  return list[0].bullets.map((b, i) => ({
    id: `fan-${i}`,
    label: b.length > 96 ? `${b.slice(0, 93)}…` : b,
    bullets: [b],
  }));
}

// FlashCard Component
const FlashCard = ({
  step,
  stepNumber,
  isActive,
  speakText,
  speakPayload,
  isPlaying,
  transformedImage,
  originalImageUrl,
  totalSteps,
  onStep11Trigger,
  isStep11 = false,
  onShowUpload,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [expandedSubId, setExpandedSubId] = useState(null);
  /** Steps 1–2: materials / tools+colors start collapsed */
  const [suppliesOpen, setSuppliesOpen] = useState(false);

  useEffect(() => {
    if (isActive) {
      setSuppliesOpen(false);
      setExpandedSubId(null);
    }
  }, [isActive, stepNumber, isStep11]);

  const isEarlyStep = stepNumber <= 2 && !isStep11;
  const showSuppliesBlock = stepNumber < 10 && !isStep11;

  const displayInstruction = useMemo(() => {
    let t = (step.instruction || '').trim();
    if (stepNumber === 2) {
      t = t.replace(/look\s+at\s+(the\s+)?artwork\s+above!?\.?\s*/gi, 'In your artwork, top left, the grid box is there. ');
    }
    return t;
  }, [step.instruction, stepNumber]);

  const substeps = useMemo(() => {
    if (isEarlyStep || isStep11) return [];
    let list = parseInstructionSubsteps(step.instruction, { earlySteps: false });
    list = fanOutMultiBulletsForEarlySteps(list, stepNumber, isStep11);
    return list;
  }, [step.instruction, stepNumber, isStep11, isEarlyStep]);

  const useAccordion =
    !isStep11 &&
    stepNumber > 2 &&
    (substeps.length > 1 || (substeps.length === 1 && substeps[0]?.bullets?.length > 1));

  const handleSpeak = () => {
    const text =
      speakPayload ?? (isEarlyStep ? displayInstruction : step.instruction || '');
    if (typeof speakText === 'function') speakText(text);
  };

  const handleCardClick = () => {
    setIsFlipped(!isFlipped);
  };

  if (!isActive) return null;

  return (
    <div className="absolute inset-0 perspective-1000">
      <div 
        className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        onClick={handleCardClick}
      >
        {/* Front of Card */}
        <div className="absolute inset-0 backface-hidden">
          <div className="w-full h-full rounded-xl md:rounded-2xl border-2 md:border-4 shadow-xl p-3 md:p-6 flex flex-col justify-center items-center text-center ring-1 ring-amber-200/50 bg-gradient-to-br from-amber-50/95 via-amber-50/70 to-amber-100/50 border-amber-200/80">
            <div className="mb-2 md:mb-4">
              <div className="w-12 h-12 md:w-16 md:h-16 text-white rounded-full flex items-center justify-center text-lg md:text-2xl font-bold mx-auto mb-2 md:mb-3 bg-gradient-to-br from-amber-500 to-amber-600 shadow-md">
                {stepNumber}
              </div>
              <h3 className="text-base md:text-2xl font-bold mb-1 md:mb-2 text-zinc-800">{step.title}</h3>
              <p className="text-xs md:text-sm text-zinc-600">
                {stepNumber === 1 ? 'Click to see what you need!' : isStep11 ? 'Click to share your masterpiece!' : 'Click to flip and see instructions!'}
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-4 mt-2 md:mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpeak();
                }}
                className="p-2 md:p-3 rounded-full transition-all bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md"
              >
                {isPlaying ? <FaVolumeMute size={16} className="md:w-5 md:h-5" /> : <FaVolumeUp size={16} className="md:w-5 md:h-5" />}
              </button>

              <div className="text-zinc-600">
                <div className="text-xs md:text-sm">{step.time_minutes} min</div>
                <div className="text-[10px] md:text-xs">Step {stepNumber} of {totalSteps}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Back of Card */}
        <div className="absolute inset-0 backface-hidden rotate-y-180">
          <div className="w-full h-full rounded-xl md:rounded-2xl border-2 md:border-4 shadow-xl p-3 md:p-6 overflow-y-auto ring-1 border-amber-200/85 ring-amber-100/50 bg-gradient-to-b from-amber-50/90 via-amber-50/40 to-amber-100/30">
            <div className="flex items-start justify-between mb-2 md:mb-4">
              <h3 className="text-base md:text-xl font-bold flex-1 pr-2 text-zinc-800">{step.title}</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSpeak();
                }}
                className="p-1 flex-shrink-0 text-amber-800/90 hover:text-amber-950"
              >
                {isPlaying ? <FaVolumeMute size={14} className="md:w-4 md:h-4" /> : <FaVolumeUp size={14} className="md:w-4 md:h-4" />}
              </button>
            </div>

            <div className="space-y-2 md:space-y-4" onClick={(e) => e.stopPropagation()}>
              {isEarlyStep ? (
                <div className="rounded-xl border-2 border-amber-200/70 bg-gradient-to-br from-amber-50/95 to-amber-100/40 px-3 py-3 md:px-4 md:py-4 shadow-sm ring-1 ring-amber-100/60">
                  <p className="text-zinc-700 text-sm md:text-lg leading-relaxed tracking-wide">{displayInstruction}</p>
                </div>
              ) : isStep11 ? (
                <div className="rounded-xl border-2 border-amber-200/70 bg-gradient-to-br from-amber-50/95 to-amber-100/40 px-3 py-3 md:px-4 md:py-4 shadow-sm ring-1 ring-amber-100/60">
                  <p className="text-zinc-700 text-sm md:text-lg leading-relaxed tracking-wide whitespace-pre-line">
                    {step.instruction}
                  </p>
                </div>
              ) : useAccordion ? (
                <div className="space-y-2">
                  <p className="text-[10px] md:text-xs text-zinc-600 mb-1 leading-snug">Tap a step to read it — one small part at a time.</p>
                  {substeps.map((sub) => {
                    const open = expandedSubId === sub.id;
                    return (
                      <div
                        key={sub.id}
                        className="rounded-xl border-2 border-amber-200/70 bg-amber-50/50 overflow-hidden shadow-sm ring-1 ring-amber-100/50"
                      >
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 md:px-3.5 md:py-2.5 text-xs md:text-sm font-medium text-zinc-700 leading-snug hover:bg-amber-100/50 transition-colors"
                          onClick={() => setExpandedSubId(open ? null : sub.id)}
                        >
                          <span className="flex-1 pr-2 line-clamp-2">{sub.label}</span>
                          <FaChevronDown
                            className={`w-3 h-3 md:w-3.5 md:h-3.5 text-amber-800/60 flex-shrink-0 mt-0.5 transition-transform duration-200 ${
                              open ? 'rotate-180' : ''
                            }`}
                            aria-hidden
                          />
                        </button>
                        {open && (
                          <div className="border-t border-amber-200/60 px-3 pb-3 pt-2 md:px-4 md:pb-4 bg-white/60">
                            <ul className="list-disc pl-4 space-y-1.5 text-sm md:text-base text-zinc-700 leading-relaxed marker:text-amber-300">
                              {sub.bullets.map((b, i) => (
                                <li key={i}>{b}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : substeps[0]?.bullets?.length > 1 ? (
                <div className="rounded-xl border-2 border-amber-200/70 bg-gradient-to-br from-amber-50/95 to-amber-100/40 px-3 py-3 md:px-4 md:py-4 shadow-sm ring-1 ring-amber-100/60">
                  <ul className="list-disc pl-4 space-y-1.5 text-sm md:text-base text-zinc-700 leading-relaxed marker:text-amber-300">
                    {substeps[0].bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-xl border-2 border-amber-200/70 bg-gradient-to-br from-amber-50/95 to-amber-100/40 px-3 py-3 md:px-4 md:py-4 shadow-sm ring-1 ring-amber-100/60">
                  <p className="text-zinc-700 leading-relaxed text-sm md:text-lg">{step.instruction}</p>
                </div>
              )}

              {/* Tools / materials — steps 1–9: collapsed until opened */}
              {showSuppliesBlock && (
                <div className="rounded-xl border-2 border-amber-200/70 overflow-hidden bg-amber-50/35 ring-1 ring-amber-100/50 shadow-sm">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 md:px-3.5 md:py-2.5 font-medium text-zinc-700 hover:bg-amber-100/40 transition-colors text-xs md:text-sm leading-snug"
                    onClick={() => setSuppliesOpen((o) => !o)}
                  >
                    <span className="line-clamp-2 pr-1">
                      {stepNumber === 1 ? "Materials you'll need" : 'Tools & colors for this step'}
                    </span>
                    <FaChevronDown
                      className={`w-3 h-3 md:w-3.5 md:h-3.5 text-amber-800/60 flex-shrink-0 mt-0.5 transition-transform duration-200 ${
                        suppliesOpen ? 'rotate-180' : ''
                      }`}
                      aria-hidden
                    />
                  </button>
                  {suppliesOpen && (
                    <div className="border-t border-amber-200/60 p-3 md:p-4 space-y-3 md:space-y-4 bg-white/55">
                      <div>
                        <h5 className="font-semibold text-zinc-800 mb-2 md:mb-3 text-sm md:text-base">
                          {stepNumber === 1 ? 'Gather these' : "Tools you'll need"}
                        </h5>
                        <div className={`grid ${stepNumber === 1 ? 'grid-cols-1' : 'flex flex-wrap'} gap-1.5 md:gap-2`}>
                          {step.tools.map((tool, index) => (
                            <div
                              key={index}
                              className={`${
                                stepNumber === 1
                                  ? 'flex items-center gap-2 md:gap-3 bg-amber-50/90 p-2 md:p-3 rounded-lg border border-amber-200/70 shadow-sm'
                                  : 'bg-amber-100/80 text-zinc-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium border border-amber-200/50'
                              }`}
                            >
                              {stepNumber === 1 && (
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full flex items-center justify-center text-[10px] md:text-xs font-bold flex-shrink-0">
                                  {index + 1}
                                </div>
                              )}
                              <span className={`${stepNumber === 1 ? 'text-zinc-700 font-medium text-xs md:text-sm' : ''}`}>{tool}</span>
                            </div>
                          ))}
                        </div>
                        {stepNumber === 1 && (
                          <div className="mt-2 md:mt-3 p-2 md:p-3 bg-amber-50/80 rounded-lg border border-amber-200/60">
                            <p className="text-zinc-700 text-xs md:text-sm font-medium text-center">
                              When you have everything ready, click the next card to start creating!
                            </p>
                          </div>
                        )}
                      </div>
                      {stepNumber > 1 && (
                        <div>
                          <h5 className="font-semibold text-zinc-800 mb-1.5 md:mb-2 text-sm md:text-base">Colors to use</h5>
                          <div className="flex flex-wrap gap-1.5 md:gap-2">
                            {step.colors.map((color, index) => (
                              <span
                                key={index}
                                className="bg-amber-100/90 text-zinc-700 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium border border-amber-200/50"
                              >
                                {color}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between text-xs md:text-sm text-zinc-600 pt-2 border-t border-amber-200/60">
                <span>{step.time_minutes} minutes</span>
                <span>Step {stepNumber} of {totalSteps}</span>
              </div>

              {/* Step 11 Upload Button */}
              {isStep11 && (
                <div className="mt-3 md:mt-4 space-y-2 md:space-y-3">
                  <div className="text-center">
                    <p className="text-zinc-600 text-xs md:text-sm font-medium mb-3 md:mb-4">
                      We're so proud of you for creating something beautiful!
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowUpload();
                      }}
                      className="bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-4 md:px-8 py-2 md:py-4 rounded-lg md:rounded-xl font-bold text-sm md:text-xl flex items-center justify-center gap-2 md:gap-3 shadow-lg transform hover:scale-[1.02] transition-all border border-amber-400/40"
                    >
                      <FaUpload size={16} className="md:w-6 md:h-6" /> <span className="hidden md:inline">Upload Your Artwork</span><span className="md:hidden">Upload</span>
                    </button>
                  </div>
                </div>
              )}

              {step.overlay && transformedImage && (
                <div className="relative w-full h-32 rounded-lg overflow-hidden border border-amber-200/70 mt-4">
                  <img 
                    src={transformedImage} 
                    alt="Step reference" 
                    className="w-full h-full object-cover"
                  />
                  <svg className="absolute inset-0 w-full h-full">
                    {/* SVG overlay hints would go here */}
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepByStepLearning;
